import React from 'react';
import './App.css';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import CareersPage from './pages/CareersPage';
import NewsroomPage from './pages/NewsroomPage';
import HowItWorksSection from './components/HowItWorksSection';
import WaitlistModal from './components/WaitlistModal';

function AppContent() {
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  // Scroll to top when route changes
  React.useEffect(() => {
    // Immediate scroll to top
    window.scrollTo(0, 0);
    
    // Also force it after a delay to ensure it works
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 50);
  }, [location.pathname]);

  // Scroll to top on page load/refresh
  React.useEffect(() => {
    // Disable scroll restoration
    if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    
    // Force scroll to top immediately
    window.scrollTo(0, 0);
    
    // Also set it after a small delay to ensure it works
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 50);
  }, []);

  // Removed shooting stars per request

  return (
    <div className="App">
      <Navbar />
      <main>
        <Routes>
          <Route path="/" element={
            <>
              <section className="hero">
                <h1 className="hero__title">A more authentic way to connect</h1>
                <p className="hero__subtitle">Wrap is where real connections grow. Answer daily photo or video prompts to keep your profile alive, and show who you are.</p>
                <div className="hero__cta">
                  <button 
                    className="store-btn btn-lg btn--inverted btn--wide btn--arrow" 
                    aria-label="Join the waitlist"
                    type="button"
                    onClick={() => setIsModalOpen(true)}
                  >
                    <span className="store-btn__label">Join waitlist</span>
                  </button>
                </div>
              </section>
              <section className="spacer" />
              <HowItWorksSection />
              <section className="spacer spacer--large" />
              <FAQSection />
              <section className="spacer spacer--small" />
              <WaitlistSection />
            </>
          } />
          <Route path="/newsroom" element={<NewsroomPage />} />
          <Route path="/careers" element={<CareersPage />} />
        </Routes>
      </main>
      <Footer />
      <WaitlistModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;

function FAQSection() {
  const ref = React.useRef<HTMLElement | null>(null);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && el.classList.add('in-view')),
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={ref as any} className="faq reveal">
      <div className="faq__grid">
        <div className="faq__left">
          <div className="kicker"><span className="diamond" aria-hidden /> FAQs</div>
          <h2 className="title-xl">
            <span className="nowrap">Have questions?</span><br />
            We’d love to hear.
          </h2>
          <button className="store-btn btn-lg btn--inverted btn--wide btn--arrow" type="button">Get in touch</button>
        </div>
        <FAQList />
      </div>
    </section>
  );
}

function WaitlistSection() {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const ref = React.useRef<HTMLElement | null>(null);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.classList.add('in-view');
          }
        });
      },
      { root: null, rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref as any} className="waitlist reveal">
      <h2 className="title-xl waitlist__title">Join the waitlist to get access sooner</h2>
      <div className="waitlist__cta">
        <button 
          className="store-btn btn-lg btn--inverted btn--wide btn--arrow" 
          type="button"
          onClick={() => setIsModalOpen(true)}
        >
          Join waitlist
        </button>
      </div>
      <WaitlistModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </section>
  );
}

const FAQList: React.FC = () => {
  const [openIndex, setOpenIndex] = React.useState<number | null>(null);
  const faqs = [
    {
      q: 'What platforms will Wrap be on?',
      a: 'We’re building for iOS and Android first, with a web experience following later.'
    },
    {
      q: 'How is my data handled?',
      a: 'Privacy and safety come first. We only store what’s needed to operate your account and features, and you can request deletion at any time.'
    },
    {
      q: 'How much does Wrap cost?',
      a: 'We’ll have a free tier at launch with optional premium features. Pricing will be announced closer to release.'
    },
    {
      q: 'How do wraps work?',
      a: 'Answer daily prompts with quick photo or video posts. Your profile stays active when you participate, so others see the real you.'
    }
  ];

  return (
    <div className="faq__right">
      {faqs.map((item, idx) => {
        const isOpen = openIndex === idx;
        return (
          <div key={item.q} className={`faq__card${isOpen ? ' open' : ''}`}>
            <button
              className="faq__item"
              type="button"
              aria-expanded={isOpen}
              onClick={() => setOpenIndex(isOpen ? null : idx)}
            >
              <span className="faq__question">{item.q}</span>
              <span className="faq__plus" aria-hidden>{isOpen ? '−' : '+'}</span>
            </button>
            <div className="faq__panel" role="region">
              <p className="faq__answer">{item.a}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
};